/**
 * DesktopDiscoveryPanelAdapter
 * Thin adapter that instantiates useDiscoveryFiltersState + derived facets,
 * then renders FilterPanelContent or SearchPanelContent based on current panel mode.
 *
 * This mirrors the same state setup used by MobileDiscoverySystem in DiscoverStickybar.
 * No new business logic — only prop plumbing from existing hooks.
 */
import * as React from 'react';
import { supabase } from '../../../lib/supabase';
import { getDiscoverFacetsV3 } from '@bright-web-skeleton/discover-contract';
import type {
  NormalizedIndustryOption,
  FacetResponseV3,
  TaxonomyFacetOption,
} from '@bright-web-skeleton/discover-contract';
import {
  useDiscoveryFiltersState,
  type RangeFilter,
} from '../../../hooks/useDiscoveryFiltersState';
import { useDerivedFacets } from '../../../hooks/useDerivedFacets';
import { FilterPanelContent, SearchPanelContent } from '@bright-web-skeleton/ui';
import {
  getIndustryLabel,
  getCategoryLabel,
} from '../../../mock/discoveryMockData';
import type { RightPanelMode } from '@bright-web-skeleton/ui';

export interface DesktopDiscoveryPanelAdapterProps {
  /** Current panel mode (only 'search' | 'filters' are handled) */
  panel: RightPanelMode;
  /** Committed search query from URL */
  searchQuery: string;
  /** Committed industry IDs from URL */
  selectedIndustryIds: string[];
  /** Committed category IDs from URL */
  selectedCategoryIds: string[];
  /** Committed subcategory IDs from URL */
  selectedSubcategoryIds: string[];
  /** V3 facets (base / unfiltered) */
  facetsV3?: FacetResponseV3 | null;
  /** Industries for search suggestions */
  industries: NormalizedIndustryOption[];
  /** Committed ranges from URL */
  committedPriceRange: { min: number | null; max: number | null };
  committedCommissionRange: { min: number | null; max: number | null };
  committedDiscountRange: { min: number | null; max: number | null };
  /** Coverage bounds from DB for slider limits */
  coverageBounds?: {
    priceBounds: { min: number; max: number };
    commissionBounds: { min: number; max: number };
    discountBounds: { min: number; max: number };
  };
  /** Atomic commit to URL (same as onCommitAll in DiscoverPage) */
  onCommitAll: (params: {
    q?: string;
    industry_ids?: string[];
    category_ids?: string[];
    subcategory_ids?: string[];
    price_min?: number | null;
    price_max?: number | null;
    commission_min?: number | null;
    commission_max?: number | null;
    discount_min?: number | null;
    discount_max?: number | null;
  }) => void;
  /** Clear all committed filters */
  onClearAll: () => void;
  /** Set industry IDs */
  onSetIndustryIds?: (ids: string[]) => void;
  /** Set category IDs */
  onSetCategoryIds?: (ids: string[]) => void;
  /** Search commit (for search panel) */
  onSearchCommit: (query: string) => void;
  /** Total result count for filter footer */
  totalResultCount?: number;
}

export const DesktopDiscoveryPanelAdapter: React.FC<DesktopDiscoveryPanelAdapterProps> = ({
  panel,
  searchQuery,
  selectedIndustryIds,
  selectedCategoryIds,
  selectedSubcategoryIds,
  facetsV3,
  industries,
  committedPriceRange,
  committedCommissionRange,
  committedDiscountRange,
  coverageBounds,
  onCommitAll,
  onClearAll,
  onSetIndustryIds,
  onSetCategoryIds,
  onSearchCommit,
  totalResultCount = 0,
}) => {
  // Same filterState hook as MobileDiscoverySystem
  const filterState = useDiscoveryFiltersState({
    searchQuery,
    industryIds: selectedIndustryIds,
    categoryIds: selectedCategoryIds,
    commissionRange: committedCommissionRange,
    priceRange: committedPriceRange,
    discountRange: committedDiscountRange,
  });

  // Derived facets for filter drawer
  const derivedFacets = useDerivedFacets({
    query: filterState.draft.draftQuery,
    industryIds: filterState.draft.industryIds,
    categoryIds: filterState.draft.categoryIds,
    subcategoryIds: filterState.draft.subcategoryIds,
    commissionMin: filterState.draft.commissionRange.min ?? undefined,
    commissionMax: filterState.draft.commissionRange.max ?? undefined,
    priceMin: filterState.draft.priceRange.min ?? undefined,
    priceMax: filterState.draft.priceRange.max ?? undefined,
    discountMin: filterState.draft.discountRange.min ?? undefined,
    discountMax: filterState.draft.discountRange.max ?? undefined,
  });

  // Fetch category facets based on draft industry IDs only (prevents collapse)
  const [draftCategoryFacetsV3, setDraftCategoryFacetsV3] = React.useState<FacetResponseV3 | null>(null);
  React.useEffect(() => {
    const draftIndustryIds = filterState.draft.industryIds;
    if (draftIndustryIds.length === 0) {
      setDraftCategoryFacetsV3(null);
      return;
    }
    let cancelled = false;
    getDiscoverFacetsV3(supabase, {
      q: '', subjects: [], geo: [], brand_categories: [], psychographics: [],
      brand_geographic_focus: [], sort: 'relevance' as any, page: 1, pageSize: 50,
      industry_ids: draftIndustryIds, category_ids: [], subcategory_ids: [], hashtag_ids: [],
    }).then((result) => { if (!cancelled) setDraftCategoryFacetsV3(result); }).catch(() => {});
    return () => { cancelled = true; };
  }, [filterState.draft.industryIds]);

  // Fetch subcategory facets based on draft industry + category IDs
  const [draftSubcategoryFacetsV3, setDraftSubcategoryFacetsV3] = React.useState<FacetResponseV3 | null>(null);
  React.useEffect(() => {
    const draftIndustryIds = filterState.draft.industryIds;
    const draftCategoryIds = filterState.draft.categoryIds;
    if (draftIndustryIds.length === 0 && draftCategoryIds.length === 0) {
      setDraftSubcategoryFacetsV3(null);
      return;
    }
    let cancelled = false;
    getDiscoverFacetsV3(supabase, {
      q: '', subjects: [], geo: [], brand_categories: [], psychographics: [],
      brand_geographic_focus: [], sort: 'relevance' as any, page: 1, pageSize: 50,
      industry_ids: draftIndustryIds, category_ids: draftCategoryIds, subcategory_ids: [], hashtag_ids: [],
    }).then((result) => { if (!cancelled) setDraftSubcategoryFacetsV3(result); }).catch(() => {});
    return () => { cancelled = true; };
  }, [filterState.draft.industryIds, filterState.draft.categoryIds]);

  // Validity cleanup
  React.useEffect(() => {
    const validCategoryIds = derivedFacets.categoryFacets.map((f) => f.id);
    filterState.cleanupInvalidCategories(validCategoryIds);
  }, [derivedFacets.categoryFacets, filterState.cleanupInvalidCategories]);

  React.useEffect(() => {
    const validSubcategoryIds = derivedFacets.subcategoryFacets.map((f) => f.id);
    filterState.cleanupInvalidSubcategories(validSubcategoryIds);
  }, [derivedFacets.subcategoryFacets, filterState.cleanupInvalidSubcategories]);

  // Apply handler (same as MobileDiscoverySystem handleApply)
  const handleApply = () => {
    onCommitAll({
      q: filterState.draft.draftQuery,
      industry_ids: filterState.draft.industryIds,
      category_ids: filterState.draft.categoryIds,
      subcategory_ids: filterState.draft.subcategoryIds,
      price_min: filterState.draft.priceRange.min,
      price_max: filterState.draft.priceRange.max,
      commission_min: filterState.draft.commissionRange.min,
      commission_max: filterState.draft.commissionRange.max,
      discount_min: filterState.draft.discountRange.min,
      discount_max: filterState.draft.discountRange.max,
    });
    filterState.commitAll();
  };

  const handleClear = () => filterState.clearDraft();

  const handleClearAll = () => {
    filterState.clearAll();
    onClearAll();
  };

  // Search execute handler
  const handleExecuteSearch = (explicitQuery?: string) => {
    const q = explicitQuery ?? filterState.draft.draftQuery;
    onCommitAll({
      q,
      industry_ids: filterState.draft.industryIds,
      category_ids: filterState.draft.categoryIds,
      subcategory_ids: filterState.draft.subcategoryIds,
    });
    if (explicitQuery !== undefined) filterState.setDraftQuery(explicitQuery);
    filterState.commitAll();
  };

  // Search: add/remove taxonomy handlers
  const handleExecuteWithSelection = (
    industryId?: string,
    categoryId?: string,
    subcategoryId?: string,
    explicitQuery?: string,
  ) => {
    const newIndustryIds = [...filterState.draft.industryIds];
    const newCategoryIds = [...filterState.draft.categoryIds];
    const newSubcategoryIds = [...filterState.draft.subcategoryIds];
    if (industryId && !newIndustryIds.includes(industryId)) newIndustryIds.push(industryId);
    if (categoryId && !newCategoryIds.includes(categoryId)) newCategoryIds.push(categoryId);
    if (subcategoryId && !newSubcategoryIds.includes(subcategoryId)) newSubcategoryIds.push(subcategoryId);
    const q = explicitQuery ?? filterState.draft.draftQuery;

    onCommitAll({
      q,
      industry_ids: newIndustryIds,
      category_ids: newCategoryIds,
      subcategory_ids: newSubcategoryIds,
    });
    filterState.commitAll();
  };

  // Resolve facets for panels
  const effectiveCategoryFacets = draftCategoryFacetsV3?.categories ?? facetsV3?.categories ?? [];
  const effectiveSubcategoryFacets = draftSubcategoryFacetsV3?.subcategories ?? facetsV3?.subcategories ?? [];
  const effectiveIndustryFacets = facetsV3?.industries ?? [];

  // Map to FacetItem shape expected by content components
  const toFacetItems = (items: TaxonomyFacetOption[]) =>
    items.map((i) => ({ id: i.id, label: i.label, count: i.count, miniItems: [] as string[] }));

  if (panel === 'filters') {
    return (
      <div data-filter-panel="true" className="h-full">
        <FilterPanelContent
          onApply={handleApply}
          onClear={handleClear}
          onClearAll={handleClearAll}
          searchQuery={filterState.draft.draftQuery}
          selectedIndustryIds={filterState.draft.industryIds}
          selectedCategoryIds={filterState.draft.categoryIds}
          selectedSubcategoryIds={filterState.draft.subcategoryIds}
          onToggleIndustry={(id) => {
            if (filterState.draft.industryIds.includes(id)) filterState.removeDraftIndustry(id);
            else filterState.addDraftIndustry(id);
          }}
          onToggleCategory={(id) => {
            if (filterState.draft.categoryIds.includes(id)) filterState.removeDraftCategory(id);
            else filterState.addDraftCategory(id);
          }}
          onToggleSubcategory={(id) => {
            if (filterState.draft.subcategoryIds.includes(id)) filterState.removeDraftSubcategory(id);
            else filterState.addDraftSubcategory(id);
          }}
          commissionRange={filterState.draft.commissionRange}
          priceRange={filterState.draft.priceRange}
          discountRange={filterState.draft.discountRange}
          onCommissionRangeChange={filterState.setDraftCommissionRange}
          onPriceRangeChange={filterState.setDraftPriceRange}
          onDiscountRangeChange={filterState.setDraftDiscountRange}
          industryFacets={toFacetItems(effectiveIndustryFacets)}
          categoryFacets={toFacetItems(effectiveCategoryFacets)}
          subcategoryFacets={toFacetItems(effectiveSubcategoryFacets)}
          totalResultCount={totalResultCount}
          coverageBounds={coverageBounds}
        />
      </div>
    );
  }

  if (panel === 'search') {
    return (
      <div data-search-panel="true" className="h-full">
        <SearchPanelContent
          searchQuery={filterState.draft.draftQuery}
          onSearchQueryChange={filterState.setDraftQuery}
          onExecuteSearch={handleExecuteSearch}
          selectedIndustryIds={filterState.draft.industryIds}
          selectedCategoryIds={filterState.draft.categoryIds}
          selectedSubcategoryIds={filterState.draft.subcategoryIds}
          onAddIndustry={filterState.addDraftIndustry}
          onRemoveIndustry={filterState.removeDraftIndustry}
          onAddCategory={filterState.addDraftCategory}
          onRemoveCategory={filterState.removeDraftCategory}
          onAddSubcategory={filterState.addDraftSubcategory}
          onExecuteWithSelection={handleExecuteWithSelection}
          industryFacets={toFacetItems(effectiveIndustryFacets)}
          categoryFacets={toFacetItems(effectiveCategoryFacets)}
          subcategoryFacets={toFacetItems(effectiveSubcategoryFacets)}
        />
      </div>
    );
  }

  return null;
};

export default DesktopDiscoveryPanelAdapter;
